import React, { ReactNode, useMemo } from 'react';
import { EditButton } from './EditButton';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { UnlockButton } from './UnlockButton.tsx';
import { Section } from './Section';

export type FormSectionButtonType =
  (typeof FormSectionButtonType)[keyof typeof FormSectionButtonType];

const FormSectionButtonType = {
  editButton: 'editButton',
  lockButton: 'lockButton',
};

interface FormSectionProps {
  editMode?: boolean;
  title?: ReactNode;
  description?: ReactNode;
  buttonType?: FormSectionButtonType;
  onClickButton?: () => void;
  canSeeButton?: boolean;
  editActions?: ReactNode;
  children: React.ReactNode;
  className?: string;
  id?: string;
}

export const FormSection: React.FC<FormSectionProps> = ({
  editMode,
  title,
  description,
  buttonType = 'editButton',
  onClickButton,
  canSeeButton,
  editActions,
  children,
  className,
  id,
}) => {
  const [ref] = useAutoAnimateConditional<HTMLDivElement>();

  const formSectionButton = useMemo(() => {
    if (onClickButton === undefined) return;

    switch (buttonType) {
      case 'lockButton':
        return <UnlockButton onClickButton={onClickButton} />;
      case 'hideButton':
        return;
      case 'editButton':
      default:
        return <EditButton onClickButton={onClickButton} />;
    }
  }, [onClickButton, buttonType]);

  const renderEditButton = useMemo(() => {
    if (onClickButton === undefined) return;
    if (!canSeeButton) return;

    if (!editMode && canSeeButton) {
      return formSectionButton;
    }
  }, [editMode, onClickButton, canSeeButton, formSectionButton]);

  return (
    <Section className={className} id={id}>
      {title && (
        <div className="mb-6 flex w-full flex-row flex-wrap items-center justify-between whitespace-nowrap">
          <h2 className="truncate">{title}</h2>
          <div className="ml-auto">
            {editMode && editActions ? editActions : renderEditButton}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2" ref={ref}>
        {description && <p>{description}</p>}
        {children}
      </div>
    </Section>
  );
};
